import { GrowthBookClient } from '@growthbook/growthbook'

import { NETWORK_TIMEOUT_MS } from '../constants'

const CLIENT_KEY = process.env.GROWTHBOOK_CLIENT_KEY || ''
const API_HOST = process.env.GROWTHBOOK_API_HOST || 'http://localhost:3300'

const REFRESH_INTERVAL_MS = 60 * 1000
const INITIAL_RETRY_DELAY_MS = 3 * 1000
const MAX_RETRY_DELAY_MS = 60 * 1000
const MAX_RETRY_JITTER_MS = 1 * 1000

let _clientPromise: Promise<GrowthBookClient> | null = null

export function getClient(): Promise<GrowthBookClient> {
  if (!_clientPromise) {
    _clientPromise = initializeClient().catch((e) => {
      // initializeClient is designed never to reject, but if a future edit ever
      // adds a throwing path before the first await we don't want to (a) poison
      // the cache with a rejected promise, or (b) surface an unhandled rejection
      // for any caller that doesn't await. Reset the cache and log.
      _clientPromise = null
      console.error('[shared-feature-flags] initializeClient rejected unexpectedly', e)
      // Return a fresh un-initialized GrowthBookClient so the awaited promise
      // still resolves to a valid type, downstream createScopedInstance works,
      // and reads fall through to declared defaults — same degradation as the
      // no-key / outage paths. No unhandled rejection.
      return new GrowthBookClient({ apiHost: API_HOST, clientKey: CLIENT_KEY })
    })
  }
  return _clientPromise
}

function schedulePeriodicRefresh(client: GrowthBookClient): void {
  setInterval(async () => {
    try {
      await client.refreshFeatures()
    } catch (e) {
      console.error('[shared-feature-flags] refreshFeatures failed', e)
    }
  }, REFRESH_INTERVAL_MS)
}

async function tryInit(client: GrowthBookClient): Promise<Error | null> {
  try {
    const response = await client.init({ timeout: NETWORK_TIMEOUT_MS })
    if (response?.error) {
      return response.error
    }
    return null
  } catch (e) {
    if (e instanceof Error) {
      return e
    }
    return new Error(`[shared-feature-flags] unknown error initializing GrowthBook client: ${e}`)
  }
}

async function retryInitInBackground(client: GrowthBookClient) {
  let attempt = 1
  let initialized = false
  while (!initialized) {
    const delay = Math.min(MAX_RETRY_DELAY_MS, INITIAL_RETRY_DELAY_MS * 2 ** (attempt - 1))
    const jitter = Math.random() * MAX_RETRY_JITTER_MS
    await new Promise((resolve) => setTimeout(resolve, delay + jitter))

    const error = await tryInit(client)
    if (!error) {
      schedulePeriodicRefresh(client)
      initialized = true
    } else {
      console.error('[shared-feature-flags] client init failed', error)
      attempt++
    }
  }
}

async function initializeClient(): Promise<GrowthBookClient> {
  const client = new GrowthBookClient({
    apiHost: API_HOST,
    clientKey: CLIENT_KEY,
  })

  if (!CLIENT_KEY) {
    // Permanent misconfig — retrying won't help. Skip init; reads fall back to
    // declared defaults via the `?? featuresWithDefaultValue[key]` path in
    // withFeatureFlags.
    console.warn(
      '[shared-feature-flags] GROWTHBOOK_CLIENT_KEY is not set; serving declared defaults only'
    )
    return client
  }

  const error = await tryInit(client)
  if (!error) {
    schedulePeriodicRefresh(client)
    return client
  }

  // First attempt failed: resolve with the un-initialized client so callers
  // aren't blocked on a GrowthBook outage (they degrade to declared defaults),
  // and keep retrying in the background with exponential backoff + jitter.
  console.error('[shared-feature-flags] client init failed', error)
  void retryInitInBackground(client)
  return client
}
